import { Fragment } from 'react';

import { IKnmPreview } from 'ApiPackage/SearchModule/interfaces/IKnmPreview';
import { formatDateLocale, formatDate } from 'CorePackage/MainModule/common/formatDate';
import {
  DATE_FORMAT_FULL,
  DATE_FORMAT_LONG_MINUTE,
  DATE_FORMAT_LONG,
} from 'CorePackage/MainModule/constants/dateFormat';

import { getNewMappedStatus } from '../common/getNewMappedStatus';
import { KnmSubtitle } from './KnmSubtitle';
import styles from './styles.module.scss';
import { fetchReasonTitle } from '../../../CorePackage/MainModule/common/fieldUtils';
import { parseErknmValue } from '../common/parseErknmValue';

interface IKnmCommonProps {
  passportData?: IKnmPreview;
}

export const KnmCommon = ({ passportData }: IKnmCommonProps) => {
  const renderFzName = (fzName: string) => {
    const splitedName = fzName.split(' ');
    if (splitedName.length > 1) return splitedName.join('-');
    return `${fzName}-ФЗ`;
  };
  return (
    <div className={styles.ColumnContent}>
      <KnmSubtitle title="Общий состав атрибутов паспорта КНМ">
        {passportData?.erpId && (
          <div className={styles.Row}>
            <div className={styles.ColText}>
              Учетный номер КНМ в соответствии с постановлением Правительства РФ от 28 апреля 2015 г. N 415 &quot;О
              Правилах формирования и ведения единого реестра проверок&quot;
            </div>
            <div className={styles.ColValue}>{passportData?.erpId}</div>
          </div>
        )}
        {passportData?.erpCreateDate && (
          <div className={styles.Row}>
            <div className={styles.ColText}>
              Дата получения учетного номера КНМ в соответствии с постановлением Правительства РФ от 28 апреля 2015 г. N
              415 «О Правилах формирования и ведения единого реестра проверок»
            </div>
            <div className={styles.ColValue}>
              {formatDateLocale({ date: passportData?.erpCreateDate, dateFormat: DATE_FORMAT_FULL })}
            </div>
          </div>
        )}
        {passportData?.typeName && (
          <div className={styles.Row}>
            <div className={styles.ColText}>Тип КНМ</div>
            <div className={styles.ColValue}>{passportData?.typeName}</div>
          </div>
        )}
        <div className={styles.Row}>
          <div className={styles.ColText}>Федеральный закон, в соответствии с которым проводится КНМ</div>
          <div className={styles.ColValue}>{passportData?.fzName && renderFzName(passportData?.fzName)}</div>
        </div>
        {(passportData?.auditedPersonAuthority || passportData?.auditedPersonAuthority?.length) && (
          <div className={styles.Row}>
            <div className={styles.ColText}>Полномочия проверяемого лица, реализация которых подлежит проверке</div>
            <div className={styles.ColValue}>{passportData?.auditedPersonAuthority.join(', ')}</div>
          </div>
        )}
        {passportData?.status && (
          <div className={styles.Row}>
            <div className={styles.ColText}>Статус КНМ</div>
            <div className={styles.ColValue}>
              {getNewMappedStatus(
                passportData?.status,
                !!(
                  passportData?.approve?.impossibleConductionReason ||
                  passportData?.objects?.some(item =>
                    item.result?.resultInformation?.some(
                      resultInfo => resultInfo?.typeCode === 'UNIMPOSSIBLE_REASON_I',
                    ),
                  )
                ),
              )}
            </div>
          </div>
        )}
        {passportData?.created && (
          <div className={styles.Row}>
            <div className={styles.ColText}>Дата регистрации в ФГИС ЕРП</div>
            <div className={styles.ColValue}>
              {formatDateLocale({ date: passportData?.created, dateFormat: DATE_FORMAT_LONG_MINUTE })}
            </div>
          </div>
        )}
        {passportData?.approve && passportData?.approve.impossibleConductionReason && (
          <div className={styles.Row}>
            <div className={styles.ColText}>Сведения о причинах невозможности проведения КНМ</div>
            <div className={styles.ColValue}>{passportData?.approve.impossibleConductionReason}</div>
          </div>
        )}
      </KnmSubtitle>

      {passportData?.classification && (
        <>
          <KnmSubtitle title="Общий состав атрибутов для классификации КНМ">
            {passportData?.classification.carryoutTypeName && (
              <div className={styles.Row}>
                <div className={styles.ColText}>Форма проведения КНМ</div>
                <div className={styles.ColValue}>{passportData?.classification.carryoutTypeName}</div>
              </div>
            )}
            {passportData?.classification.supervisionName && (
              <div className={styles.Row}>
                <div className={styles.ColText}>Вид государственного контроля (надзора)</div>
                <div className={styles.ColValue}>{passportData?.classification.supervisionName}</div>
              </div>
            )}
            {passportData?.classification && passportData?.classification.riskName && (
              <div className={styles.Row}>
                <div className={styles.ColText}>
                  Классификация КНМ по категориям риска и классу опасности (категории опасности) деятельности
                  проверяемого лица (юридического лица (ЮЛ) или индивидуального предпринимателя (ИП))
                </div>
                <div className={styles.ColValue}>{passportData?.classification.riskName}</div>
              </div>
            )}
            {passportData?.classification.noticeTypeName && (
              <div className={styles.Row}>
                <div className={styles.ColText}>Способ уведомления проверяемого лица о проведении КНМ</div>
                <div className={styles.ColValue}>{passportData?.classification.noticeTypeName}</div>
              </div>
            )}
            {passportData?.classification.noticeDate && (
              <div className={styles.Row}>
                <div className={styles.ColText}>Дата уведомления проверяемого лица о проведении КНМ</div>
                <div className={styles.ColValue}>
                  {formatDate({ date: passportData?.classification.noticeDate, dateFormat: DATE_FORMAT_LONG })}
                </div>
              </div>
            )}
            {passportData?.classification.spendingTypeName && (
              <div className={styles.Row}>
                <div className={styles.ColText}>Способ проведения контрольной закупки</div>
                <div className={styles.ColValue}>{passportData?.classification.spendingTypeName}</div>
              </div>
            )}
            {passportData?.classification.spendingDescription && (
              <div className={styles.Row}>
                <div className={styles.ColText}>Описание закупки</div>
                <div className={styles.ColValue}>{passportData?.classification.spendingDescription}</div>
              </div>
            )}
          </KnmSubtitle>
        </>
      )}

      {passportData?.approve &&
        passportData?.approve.carryoutEvents &&
        passportData?.approve.carryoutEvents.length > 0 && (
          <>
            <KnmSubtitle title="Перечень мероприятий по контролю, необходимых для достижения целей и задач проведения проверки">
              {passportData?.approve.carryoutEvents.map((event, eventIndex) => (
                <Fragment key={eventIndex}>
                  <span className={styles.SectionTitle}>
                    {`Мероприятие по контролю, необходимое для достижения целей и задач проведения КНМ №${
                      eventIndex + 1
                    }`}
                  </span>
                  {event.eventsText && (
                    <div className={styles.Row}>
                      <div className={styles.ColText}>
                        Наименование мероприятия по контролю, необходимого для достижения целей и задач проведения КНМ
                      </div>
                      <div className={styles.ColValue}>{event.eventsText}</div>
                    </div>
                  )}
                  {event.startDate && (
                    <div className={styles.Row}>
                      <div className={styles.ColText}>Дата начала проведения мероприятия</div>
                      <div className={styles.ColValue}>
                        {formatDate({ date: event.startDate, dateFormat: DATE_FORMAT_LONG })}
                      </div>
                    </div>
                  )}
                  {event.endDate && (
                    <div className={styles.Row}>
                      <div className={styles.ColText}>Дата окончания проведения мероприятия</div>
                      <div className={styles.ColValue}>
                        {formatDate({ date: event.endDate, dateFormat: DATE_FORMAT_LONG })}
                      </div>
                    </div>
                  )}
                </Fragment>
              ))}
            </KnmSubtitle>
          </>
        )}

      {passportData?.reasonsErvk && passportData?.reasonsErvk.length > 0 && (
        <KnmSubtitle title="Перечень основания проведения КНМ">
          {passportData?.reasonsErvk.map((item, index) => (
            <div key={index}>
              <div className={styles.SectionTitle}>Основание проведения КНМ</div>
              {item.reason && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>Наименование</div>
                  <div className={styles.ColValue}>{fetchReasonTitle(item)}</div>
                </div>
              )}
              {item.reason.programNumber && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>Номер программы проверок</div>
                  <div className={styles.ColValue}>{item.reason.programNumber}</div>
                </div>
              )}
              {item.reason.type.lastConductedKnmDateRequired && item.reason.lastConductedKnmDate && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>Дата</div>
                  <div className={styles.ColValue}>
                    {formatDate({ date: item.reason.lastConductedKnmDate, dateFormat: DATE_FORMAT_LONG })}
                  </div>
                </div>
              )}
              {item.reason.date && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>Дата</div>
                  <div className={styles.ColValue}>
                    {formatDate({ date: item.reason.date, dateFormat: DATE_FORMAT_LONG })}
                  </div>
                </div>
              )}
              {item.reason.requirementDetails && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>Реквизиты требования</div>
                  <div className={styles.ColValue}>{item.reason.requirementDetails}</div>
                </div>
              )}
              {item.reason.dateRequirement && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>Дата требования</div>
                  <div className={styles.ColValue}>
                    {formatDate({ date: item.reason.dateRequirement, dateFormat: DATE_FORMAT_LONG })}
                  </div>
                </div>
              )}
              {item.reason.assignmentNumber && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>Номер поручения</div>
                  <div className={styles.ColValue}>{item.reason.assignmentNumber}</div>
                </div>
              )}
              {item.reason.assignmentDate && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>Дата поручения</div>
                  <div className={styles.ColValue}>
                    {formatDate({ date: item.reason.assignmentDate, dateFormat: DATE_FORMAT_LONG })}
                  </div>
                </div>
              )}
              {item.riskIndicators?.map((indicatorValue, indicatorIndex) => {
                return (
                  <>
                    {indicatorValue.riskIndicator && (
                      <div key={indicatorIndex}>
                        <span className={styles.SectionTitle}>Индикатор риска #{indicatorIndex + 1}</span>
                        <div className={styles.Row}>
                          <div className={styles.ColText}>
                            {parseErknmValue<{ title?: string }>(indicatorValue.riskIndicator.value)?.title}
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                );
              })}
            </div>
          ))}
        </KnmSubtitle>
      )}

      <KnmSubtitle title="Сведения о согласовании проведения КНМ с органом прокуратуры">
        {passportData?.approve && passportData?.approve.startDate && (
          <div className={styles.Row}>
            <div className={styles.ColText}>Дата начала проведения КНМ</div>
            <div className={styles.ColValue}>
              {formatDate({ date: passportData?.approve.startDate, dateFormat: DATE_FORMAT_LONG })}
            </div>
          </div>
        )}
        {passportData?.approve && passportData?.approve.endDate && (
          <div className={styles.Row}>
            <div className={styles.ColText}>Дата окончания проведения КНМ</div>
            <div className={styles.ColValue}>
              {formatDate({ date: passportData?.approve.endDate, dateFormat: DATE_FORMAT_LONG })}
            </div>
          </div>
        )}
        {passportData?.approve?.startDateAsMonth !== undefined && (
          <div className={styles.Row}>
            <div className={styles.ColText}>Метка об указании только месяца проведения (КНМ) для плановых КНМ</div>
            <div className={styles.ColValue}>{passportData?.approve.startDateAsMonth ? 'Да' : 'Нет'}</div>
          </div>
        )}
        {passportData?.approve?.durationDay !== null && (
          <div className={styles.Row}>
            <div className={styles.ColText}>Срок проведения КНМ (в рабочих днях)</div>
            <div className={styles.ColValue}>{passportData?.approve?.durationDay}</div>
          </div>
        )}
        {passportData?.approve?.durationHours !== null && (
          <div className={styles.Row}>
            <div className={styles.ColText}>Срок проведения КНМ (в рабочих часах)</div>
            <div className={styles.ColValue}>{passportData?.approve?.durationHours}</div>
          </div>
        )}
        {passportData?.approve && passportData?.approve.decisionPlace && (
          <div className={styles.Row}>
            <div className={styles.ColText}>
              Место вынесения решения о согласовании проведения КНМ органом прокуратуры
            </div>
            <div className={styles.ColValue}>{passportData?.approve.decisionPlace}</div>
          </div>
        )}
        {passportData?.approve && passportData?.approve.decisionSignerTitle && (
          <div className={styles.Row}>
            <div className={styles.ColText}>Должность подписанта о согласовании проведения КНМ органа прокуратуры</div>
            <div className={styles.ColValue}>{passportData?.approve.decisionSignerTitle}</div>
          </div>
        )}
        {passportData?.approve && passportData?.approve.decisionSignerName && (
          <div className={styles.Row}>
            <div className={styles.ColText}>ФИО подписанта о согласовании проведения КНМ органа прокуратуры</div>
            <div className={styles.ColValue}>{passportData?.approve.decisionSignerName}</div>
          </div>
        )}
        {passportData?.approve && passportData?.approve.reasons && passportData?.approve.reasons[0] && (
          <div className={styles.Row}>
            <div className={styles.ColText}>
              {(passportData?.typeCode !== 'VP' &&
                'Отметка о включении плановой проверки (КНМ) в ежегодный сводный план проведения плановых проверок') ||
                'Сведения о согласовании проведения проверки с органами прокуратуры'}
            </div>
            <div className={styles.ColValue}>
              {passportData?.approve.reasons &&
                passportData?.approve.reasons[0] &&
                passportData?.approve.reasons[0].approveRequired === false &&
                'Не требует согласования'}
              {passportData?.approve.reasons &&
                passportData?.approve.reasons[0] &&
                passportData?.approve.reasons[0].approveRequired === true &&
                passportData?.approve.approved === false &&
                'Не согласовано'}
              {passportData?.approve.reasons &&
                passportData?.approve.reasons[0] &&
                passportData?.approve.reasons[0].approveRequired === true &&
                passportData?.approve.approved === true &&
                'Согласовано'}
            </div>
          </div>
        )}
        {passportData?.approve && passportData?.approve.inspectionTarget && (
          <div className={styles.Row}>
            <div className={styles.ColText}>Цели, задачи, предмет КНМ</div>
            <div className={styles.ColValue}>{passportData?.approve.inspectionTarget}</div>
          </div>
        )}
      </KnmSubtitle>

      {passportData?.approve && passportData?.approve.reasons && passportData?.approve.reasons.length > 0 && (
        <>
          <KnmSubtitle title="Перечень правовых оснований">
            {passportData?.approve.reasons.map((reason, reasonIndex) => (
              <Fragment key={reasonIndex}>
                <span className={styles.SectionTitle}>
                  {`Правовое основание проведения планового КНМ №${reasonIndex + 1}`}
                </span>
                {reason.text && (
                  <div className={styles.Row}>
                    <div className={styles.ColText}>Основание проведения КНМ</div>
                    <div className={styles.ColValue}>{reason.text}</div>
                  </div>
                )}
                {reason.date && (
                  <div className={styles.Row}>
                    <div className={styles.ColText}>
                      Дата, характеризующая один из вариантов основания проведения КНМ как основание проведения
                    </div>
                    <div className={styles.ColValue}>
                      {formatDate({ date: reason.date, dateFormat: DATE_FORMAT_LONG })}
                    </div>
                  </div>
                )}
              </Fragment>
            ))}
          </KnmSubtitle>
        </>
      )}

      {passportData?.approve && passportData?.approve.approveDocs && passportData?.approve.approveDocs.length > 0 && (
        <>
          <KnmSubtitle title="Список атрибутов документов о согласовании (продлении) проверки">
            {passportData?.approve.approveDocs.map((doc, docIndex) => (
              <Fragment key={docIndex}>
                <span className={styles.SectionTitle}>
                  {`Документ о согласовании (продлении) КНМ №${docIndex + 1}`}
                </span>
                {doc.name && (
                  <div className={styles.Row}>
                    <div className={styles.ColText}>Документ о согласовании проведения КНМ</div>
                    <div className={styles.ColValue}>{doc.name}</div>
                  </div>
                )}
                {doc.number && (
                  <div className={styles.Row}>
                    <div className={styles.ColText}>
                      Номер распоряжения или приказа руководителя (заместителя руководителя) органа контроля (надзора) о
                      проведении КНМ
                    </div>
                    <div className={styles.ColValue}>{doc.number}</div>
                  </div>
                )}
                {doc.date && (
                  <div className={styles.Row}>
                    <div className={styles.ColText}>
                      Дата распоряжения или приказа руководителя (заместителя руководителя) органа контроля (надзора) о
                      продлении срока проведения КНМ
                    </div>
                    <div className={styles.ColValue}>
                      {formatDate({ date: doc.date, dateFormat: DATE_FORMAT_LONG })}
                    </div>
                  </div>
                )}
              </Fragment>
            ))}
          </KnmSubtitle>
        </>
      )}

      {passportData?.legalBases && passportData?.legalBases.length > 0 && (
        <>
          <KnmSubtitle title="Выбранные правовые основания у проверки">
            {passportData?.legalBases.map((lb, lbIndex) => (
              <Fragment key={lbIndex}>
                <span className={styles.SectionTitle}>{`Правовое основание КНМ №${lbIndex + 1}`}</span>
                {lb.atrDate && (
                  <div className={styles.Row}>
                    <div className={styles.ColText}>Дата нормативно-правового акта</div>
                    <div className={styles.ColValue}>
                      {formatDate({ date: lb.atrDate, dateFormat: DATE_FORMAT_LONG })}
                    </div>
                  </div>
                )}
                {lb.atrNumber && (
                  <div className={styles.Row}>
                    <div className={styles.ColText}>Номер нормативно-правового акта</div>
                    <div className={styles.ColValue}>{lb.atrNumber}</div>
                  </div>
                )}
                {lb.documentName && (
                  <div className={styles.Row}>
                    <div className={styles.ColText}>Положение нормативно-правового акта</div>
                    <div className={styles.ColValue}>{lb.documentName}</div>
                  </div>
                )}
                {lb.atrI && (
                  <div className={styles.Row}>
                    <div className={styles.ColText}>Положение нормативно-правового акта.Глава</div>
                    <div className={styles.ColValue}>{lb.atrI}</div>
                  </div>
                )}
                {lb.atrII && (
                  <div className={styles.Row}>
                    <div className={styles.ColText}>Положение нормативно-правового акта.Статья</div>
                    <div className={styles.ColValue}>{lb.atrII}</div>
                  </div>
                )}
                {lb.atrIII && (
                  <div className={styles.Row}>
                    <div className={styles.ColText}>Положение нормативно-правового акта.Пункт</div>
                    <div className={styles.ColValue}>{lb.atrIII}</div>
                  </div>
                )}
                {lb.atrIV && (
                  <div className={styles.Row}>
                    <div className={styles.ColText}>Положение нормативно-правового акта.Часть</div>
                    <div className={styles.ColValue}>{lb.atrIV}</div>
                  </div>
                )}
                {lb.typePointingName && (
                  <div className={styles.Row}>
                    <div className={styles.ColText}>Тип положения нормативно-правового акта</div>
                    <div className={styles.ColValue}>{lb.typePointingName}</div>
                  </div>
                )}
              </Fragment>
            ))}
          </KnmSubtitle>
        </>
      )}
      {passportData?.requirements && passportData?.requirements.length > 0 && (
        <KnmSubtitle title="Обязательные требования">
          {passportData?.requirements.map((requirement, rIndex) => (
            <Fragment key={rIndex}>
              <span className={styles.SectionTitle}>{`Обязательное требование №${rIndex + 1}`}</span>
              {typeof requirement.name === 'string' && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>Наименование обязательного требования</div>
                  <div className={styles.ColValue}>{requirement.name}</div>
                </div>
              )}
              {typeof requirement.knoName === 'string' && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>Организация</div>
                  <div className={styles.ColValue}>{requirement.knoName}</div>
                </div>
              )}
              {typeof passportData?.classification?.supervisionName === 'string' && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>Вид государственного контроля (надзора)</div>
                  <div className={styles.ColValue}>{passportData?.classification.supervisionName}</div>
                </div>
              )}
              {requirement.documents && requirement.documents.length > 0 && (
                <KnmSubtitle title="Требования">
                  {requirement.documents.map((doc, docIndex) => (
                    <Fragment key={docIndex}>
                      <span className={styles.SectionTitle}>{`Требование №${docIndex + 1}`}</span>
                      {typeof doc.props === 'string' && (
                        <div className={styles.Row}>
                          <div className={styles.ColText}>Формулировка требования</div>
                          <div className={styles.ColValue}>{doc.props}</div>
                        </div>
                      )}
                      {typeof doc.name === 'string' && (
                        <div className={styles.Row}>
                          <div className={styles.ColText}>Наименование нормативно-правового акта</div>
                          <div className={styles.ColValue}>{doc.name}</div>
                        </div>
                      )}
                      {typeof doc.number === 'string' && (
                        <div className={styles.Row}>
                          <div className={styles.ColText}>Номер нормативно-правового акта</div>
                          <div className={styles.ColValue}>{doc.number}</div>
                        </div>
                      )}
                      {!!doc.date?.length && (
                        <div className={styles.Row}>
                          <div className={styles.ColText}>Дата нормативно-правового акта</div>
                          <div className={styles.ColValue}>
                            {formatDate({ date: doc.date, dateFormat: DATE_FORMAT_LONG })}
                          </div>
                        </div>
                      )}
                      {typeof doc.part === 'string' && (
                        <div className={styles.Row}>
                          <div className={styles.ColText}>Часть</div>
                          <div className={styles.ColValue}>{doc.part}</div>
                        </div>
                      )}
                      {typeof doc.paragraph === 'string' && (
                        <div className={styles.Row}>
                          <div className={styles.ColText}>Пункт</div>
                          <div className={styles.ColValue}>{doc.paragraph}</div>
                        </div>
                      )}
                      {typeof doc.article === 'string' && (
                        <div className={styles.Row}>
                          <div className={styles.ColText}>Статья</div>
                          <div className={styles.ColValue}>{doc.article}</div>
                        </div>
                      )}
                    </Fragment>
                  ))}
                </KnmSubtitle>
              )}
            </Fragment>
          ))}
        </KnmSubtitle>
      )}
    </div>
  );
};
